<script setup>
import { ref } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiTable from '@/Components/UI/UiTable.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import UiSegmented from '@/Components/UI/UiSegmented.vue'
import TourBookingStatusPill from '@/Components/UI/TourBookingStatusPill.vue'
import EmptyState from '@/Components/UI/EmptyState.vue'
import { useCurrency } from '@/composables/useCurrency.js'

defineOptions({ layout: AppLayout })
const { t } = useI18n()
const { formatMoney, formatDate } = useCurrency()

const props = defineProps({
  bookings: { type: Array, default: () => [] },
  meta:     { type: Object, default: () => ({}) },
  filters:  { type: Object, default: () => ({}) },
})

const search = ref(props.filters.q || '')
const status = ref(props.filters.status || 'all')

const statusOptions = [
  { value: 'all',       label: t('tourCalendar.allStatuses') },
  { value: 'pending',   label: t('tourBookings.statuses.pending') },
  { value: 'confirmed', label: t('tourBookings.statuses.confirmed') },
  { value: 'completed', label: t('tourBookings.statuses.completed') },
  { value: 'cancelled', label: t('tourBookings.statuses.cancelled') },
]

const columns = [
  { key: 'customer_name', label: t('tourBookings.customer') },
  { key: 'tour_title',    label: t('tourBookings.tour') },
  { key: 'dates',         label: t('tourBookings.dates') },
  { key: 'pax',           label: t('tourBookings.pax') },
  { key: 'status',        label: t('tourBookings.status') },
  { key: 'price',         label: t('tourBookings.price'), numeric: true },
]

let timer = null
function applyFilters() {
  clearTimeout(timer)
  timer = setTimeout(() => {
    router.get('/tour_bookings',
      { q: search.value, status: status.value === 'all' ? null : status.value },
      { preserveState: true, replace: true })
  }, 250)
}
</script>

<template>
  <PageShell :title="t('tourBookings.title')">
    <template #actions>
      <Link
        href="/tour_bookings/new"
        class="inline-flex items-center"
      >
        <UiButton variant="primary">
          {{ t('tourBookings.new') }}
        </UiButton>
      </Link>
    </template>

    <template #filters>
      <UiSegmented
        v-model="status"
        :options="statusOptions"
        @update:model-value="applyFilters"
      />
      <UiInput
        v-model="search"
        :placeholder="t('tourBookings.searchPlaceholder')"
        class="flex-1 md:flex-none md:w-80 xl:ml-auto"
        @input="applyFilters"
      />
    </template>

    <EmptyState
      v-if="!bookings.length"
      type="first-use"
      :title="t('tourBookings.empty')"
    />

    <UiTable
      v-else
      :columns="columns"
      :rows="bookings"
      :row-href="(r) => `/tour_bookings/${r.id}`"
    >
      <template #cell-dates="{ row }">
        {{ formatDate(row.starts_on) }} – {{ formatDate(row.ends_on) }}
      </template>
      <template #cell-status="{ row }">
        <TourBookingStatusPill :status="row.status" />
      </template>
      <template #cell-price="{ row }">
        <span class="fw-value tabular-nums">{{ formatMoney(row.price_quoted_cents) }}</span>
      </template>
    </UiTable>
  </PageShell>
</template>
